import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ShiftFreezeContext = createContext(null);

const POLL_MS = 60_000;            // status refresh cadence while not frozen
const POLL_FROZEN_MS = 15_000;     // tighter cadence so admin approval surfaces fast
const POLL_JITTER_MS = 10_000;     // spread clients so they don't tick in unison

export function ShiftFreezeProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);  // null = loading
  const [polling, setPolling] = useState(false);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!user || user === false) {
      setStatus(null);
      return;
    }
    try {
      const { data } = await api.get("/shift-freeze/status");
      setStatus(data);
    } catch {
      // Don't show overlay on transient failure — fail-open keeps UX usable.
      setStatus((prev) => prev || { frozen: false, reason: "status_unavailable" });
    }
  }, [user]);

  // Trigger an immediate refresh — used after the user requests an extension
  // or admin approves one, so the UI updates without waiting for the poll.
  const refresh = useCallback(() => fetchStatus(), [fetchStatus]);

  useEffect(() => {
    if (!user || user === false) return;
    setPolling(true);
    fetchStatus();
    return () => setPolling(false);
  }, [user, fetchStatus]);

  useEffect(() => {
    if (!polling) return;
    const base = status?.frozen ? POLL_FROZEN_MS : POLL_MS;
    const interval = base + Math.floor(Math.random() * POLL_JITTER_MS);
    timerRef.current = setInterval(fetchStatus, interval);
    return () => clearInterval(timerRef.current);
  }, [polling, status?.frozen, fetchStatus]);

  // React to 423 Locked responses from any API — refresh immediately so the
  // overlay appears within a second instead of waiting for the poll tick.
  useEffect(() => {
    const handler = () => { fetchStatus(); };
    window.addEventListener("shift-freeze:refresh", handler);
    return () => window.removeEventListener("shift-freeze:refresh", handler);
  }, [fetchStatus]);

  return (
    <ShiftFreezeContext.Provider value={{ status, refresh }}>
      {children}
    </ShiftFreezeContext.Provider>
  );
}

export const useShiftFreeze = () => useContext(ShiftFreezeContext) || { status: null, refresh: () => {} };
